import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useState } from 'react';

import { useDebouncedValue } from '@/hooks/useDebouncedValue.hook';
import { useMediaGallery, type UseMediaGalleryResult } from '@/hooks/useMediaGallery.hook';
import { rentalsApi } from '@/services/rentals.service';
import type {
  AdminBookingListItem,
  AdminDamageReportItem,
  AdminListingDetail,
  AdminListingListItem,
  ListingCategory,
  ListingSort,
  ListingTab,
  RentalTab,
} from '@/types/rental.types';

import { LISTING_TABS, PAGE_SIZE } from '../constants';

export const RENTALS_QUERY_KEY = 'rentals';

interface UseRentalsPageResult {
  // tab + shared state
  tab: RentalTab;
  isListingTab: boolean;
  search: string;
  category: ListingCategory | '';
  campusId: string;
  sort: ListingSort;
  campuses: { id: string; name: string }[];
  page: number;
  // data per tab
  listings: AdminListingListItem[];
  bookings: AdminBookingListItem[];
  disputes: AdminDamageReportItem[];
  total: number;
  totalPages: number;
  disputeCount: number;
  // ui state
  isLoading: boolean;
  isError: boolean;
  errorMessage: string | null;
  // mutations
  mutatingId: string | null;
  // detail drawer
  selectedListing: AdminListingListItem | null;
  detailData: AdminListingDetail | undefined;
  gallery: UseMediaGalleryResult;
  isLoadingDetail: boolean;
  isErrorDetail: boolean;
  // handlers
  onChangeTab: (tab: RentalTab) => void;
  onChangeSearch: (value: string) => void;
  onChangeCategory: (category: ListingCategory | '') => void;
  onChangeCampus: (campusId: string) => void;
  onChangeSort: (sort: ListingSort) => void;
  onChangePage: (page: number) => void;
  onView: (listing: AdminListingListItem) => void;
  onCloseDetail: () => void;
  onRemove: (listing: AdminListingListItem) => void;
  onRestore: (listing: AdminListingListItem) => void;
}

export const useRentalsPage = (): UseRentalsPageResult => {
  const queryClient = useQueryClient();

  const [tab, setTab] = useState<RentalTab>('all');
  const [search, setSearch] = useState('');
  const [category, setCategory] = useState<ListingCategory | ''>('');
  const [campusId, setCampusId] = useState('');
  const [sort, setSort] = useState<ListingSort>('newest');
  const [page, setPage] = useState(1);

  const [mutatingId, setMutatingId] = useState<string | null>(null);
  const [selectedListing, setSelectedListing] = useState<AdminListingListItem | null>(null);

  const debouncedSearch = useDebouncedValue(search, 300);
  const isListingTab = LISTING_TABS.includes(tab);

  const campusOptionsQuery = useQuery({
    queryKey: [RENTALS_QUERY_KEY, 'campuses'],
    queryFn: () => rentalsApi.listCampusOptions(),
    staleTime: 5 * 60 * 1000,
  });

  const listingsQuery = useQuery({
    queryKey: [
      RENTALS_QUERY_KEY,
      'listings',
      { tab, search: debouncedSearch, category, campusId, sort, page },
    ],
    queryFn: () =>
      rentalsApi.listListings({
        tab: tab as ListingTab,
        search: debouncedSearch || undefined,
        category: category || undefined,
        campusId: campusId || undefined,
        sort,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: isListingTab,
    placeholderData: (previous) => previous,
  });

  const bookingsQuery = useQuery({
    queryKey: [RENTALS_QUERY_KEY, 'bookings', { search: debouncedSearch, campusId, page }],
    queryFn: () =>
      rentalsApi.listActiveRentals({
        search: debouncedSearch || undefined,
        campusId: campusId || undefined,
        page,
        limit: PAGE_SIZE,
      }),
    enabled: tab === 'active',
    placeholderData: (previous) => previous,
  });

  const disputesQuery = useQuery({
    queryKey: [RENTALS_QUERY_KEY, 'disputes', { page }],
    queryFn: () => rentalsApi.listDisputes({ page, limit: PAGE_SIZE }),
    enabled: tab === 'disputes',
    placeholderData: (previous) => previous,
  });

  // Lightweight count so the Disputes tab shows its badge before it's opened.
  const disputeCountQuery = useQuery({
    queryKey: [RENTALS_QUERY_KEY, 'disputes-count'],
    queryFn: () => rentalsApi.listDisputes({ page: 1, limit: 1 }),
    staleTime: 60 * 1000,
  });

  const detailQuery = useQuery({
    queryKey: [RENTALS_QUERY_KEY, 'listing', selectedListing?.id],
    queryFn: () => rentalsApi.getListing(selectedListing!.id),
    enabled: !!selectedListing?.id,
    staleTime: 30_000,
    retry: 1,
  });

  // Photos + video for the drawer; keyed on the listing so a new one starts at its first slide.
  const gallery = useMediaGallery(
    detailQuery.data?.photos ?? selectedListing?.photos ?? [],
    detailQuery.data?.video,
    selectedListing?.id,
  );

  const invalidate = (): void => {
    void queryClient.invalidateQueries({ queryKey: [RENTALS_QUERY_KEY] });
  };

  const statusMutation = useMutation({
    mutationFn: ({ id, restore }: { id: string; restore: boolean }) =>
      restore ? rentalsApi.restoreListing(id) : rentalsApi.removeListing(id),
    onMutate: ({ id }) => setMutatingId(id),
    onSettled: () => {
      setMutatingId(null);
      invalidate();
    },
  });

  const resetAndSet =
    <T,>(setter: (value: T) => void) =>
    (value: T): void => {
      setter(value);
      setPage(1);
    };

  const activeQuery =
    tab === 'active' ? bookingsQuery : tab === 'disputes' ? disputesQuery : listingsQuery;

  return {
    tab,
    isListingTab,
    search,
    category,
    campusId,
    sort,
    campuses: campusOptionsQuery.data ?? [],
    page: activeQuery.data?.page ?? page,
    listings: listingsQuery.data?.items ?? [],
    bookings: bookingsQuery.data?.items ?? [],
    disputes: disputesQuery.data?.items ?? [],
    total: activeQuery.data?.total ?? 0,
    totalPages: activeQuery.data?.totalPages ?? 1,
    disputeCount: disputeCountQuery.data?.total ?? 0,
    isLoading: activeQuery.isLoading,
    isError: activeQuery.isError,
    errorMessage: activeQuery.error instanceof Error ? activeQuery.error.message : null,
    mutatingId,
    selectedListing,
    detailData: detailQuery.data,
    gallery,
    isLoadingDetail: detailQuery.isLoading && detailQuery.fetchStatus !== 'idle',
    isErrorDetail: detailQuery.isError,
    onChangeTab: resetAndSet(setTab),
    onChangeSearch: resetAndSet(setSearch),
    onChangeCategory: resetAndSet(setCategory),
    onChangeCampus: resetAndSet(setCampusId),
    onChangeSort: resetAndSet(setSort),
    onChangePage: setPage,
    onView: (listing) => setSelectedListing(listing),
    onCloseDetail: () => setSelectedListing(null),
    onRemove: (listing) => statusMutation.mutate({ id: listing.id, restore: false }),
    onRestore: (listing) => statusMutation.mutate({ id: listing.id, restore: true }),
  };
};
